import { Constructor } from '@angular/cdk/table';
import { FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ITimezone, TIMEZONES } from '../constants/timezones';
import { FORM_FIELDS } from 'src/app/article-content/components/article-content-form/constants/form-fields';

type WithPublishTimeFormSectionType = {
  _initPublishTime(publishTime: Date, timezone: string): void;
  _initPublishTimeValueChanges(
    getPreviousPublishTime: () => Date | null,
    getPreviousTimezone: () => string | null
  ): void;
  _buildPresetPublishTime(): Date;
  _buildTimezone(): string;
  _buildShowPublishTIme(): boolean;
  _initPublishTimeValidation(): ValidatorFn;
  get publishTime(): Date;
  get timezone(): string;
  get showPublishTime(): boolean;
};

type PublishTimeFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: {
    PUBLISH_TIME: () => string;
    TIMEZONE: () => string;
    SHOW_PUBLISH_TIME: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithPublishTimeFormSection<
  TBase extends Constructor<PublishTimeFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithPublishTimeFormSectionType;
  prototype: WithPublishTimeFormSectionType;
} & TBase {
  return class PublishTimeSectionable extends baseClass {
    timezones!: ITimezone[];
    defaultTimezone: ITimezone;

    constructor(...args: any[]) {
      super(...args);
    }

    _initPublishTime(publishTime: Date, timezone: string): void {
      this.timezones = TIMEZONES;
      this.defaultTimezone = this.timezones.find((t) => t.isDefault);

      this.form.get(this.FORM_PATHS.PUBLISH_TIME()).setValue(publishTime);
      this.form.get(this.FORM_PATHS.TIMEZONE()).setValue(timezone);
      this.form
        .get(this.FORM_PATHS.SHOW_PUBLISH_TIME())
        .setValue(publishTime || timezone ? true : false);
    }

    _initPublishTimeValueChanges(
      getPreviousPublishTime: () => Date | null,
      getPreviousTimezone: () => string | null
    ): void {
      this.form
        .get(this.FORM_PATHS.PUBLISH_TIME())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousPublishTime()) {
              this.form.get(this.FORM_PATHS.PUBLISH_TIME()).markAsPristine();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.TIMEZONE())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousTimezone()) {
              this.form.get(this.FORM_PATHS.TIMEZONE()).markAsPristine();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.SHOW_PUBLISH_TIME())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue) {
              this.form
                .get(this.FORM_PATHS.TIMEZONE())
                .setValue(this.defaultTimezone.windowsTimezoneId);
            } else {
              this.form.get(this.FORM_PATHS.PUBLISH_TIME()).setValue(null);
              this.form.get(this.FORM_PATHS.TIMEZONE()).setValue(null);
            }
          },
        });
    }

    _buildPresetPublishTime(): Date {
      return this.form.get(this.FORM_PATHS.PUBLISH_TIME()).value;
    }

    _buildTimezone(): string {
      return this.form.get(this.FORM_PATHS.TIMEZONE()).value;
    }

    _buildShowPublishTIme(): boolean {
      return this.form.get(this.FORM_PATHS.SHOW_PUBLISH_TIME()).value;
    }

    get publishTime(): Date {
      return this.form.get(this.FORM_PATHS.PUBLISH_TIME()).value;
    }
    get timezone(): string {
      return this.form.get(this.FORM_PATHS.TIMEZONE()).value;
    }
    get showPublishTime(): boolean {
      return this.form.get(this.FORM_PATHS.SHOW_PUBLISH_TIME()).value;
    }

    _initPublishTimeValidation(): ValidatorFn {
      return (formGroup: FormGroup): ValidationErrors | null => {
        const publishTimeControl = formGroup.get(FORM_FIELDS.DATE);
        const timezoneControl = formGroup.get(FORM_FIELDS.TIMEZONE);
        const showPublishTimeControl = formGroup.get(
          FORM_FIELDS.SHOW_PUBLISH_TIME
        );

        if (!showPublishTimeControl || !showPublishTimeControl.value) {
          return null;
        }

        let errors: ValidationErrors = {};

        if (!publishTimeControl.value) {
          errors[FORM_FIELDS.DATE] = true;
          publishTimeControl.setErrors(errors);
        }

        if (!timezoneControl.value) {
          errors[FORM_FIELDS.TIMEZONE] = true;
        }

        return Object.keys(errors).length === 0 ? null : errors;
      };
    }
  };
}
